import { type FC, useMemo } from 'react';
import { UiHStack, UiStack, UiText, uiStyles } from '@/lib/ui';
import { type Order, loadWaitingForPaymentOrder, orderQueryKey } from '@/api/registration';
import { useQuery } from '@tanstack/react-query';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { type ApiResponseSingle } from '@/api/tenantClient';
import QueryContainer from '@/base/QueryContainer/QueryContainer';
import BaseBlockList from '@/base/BlockList';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { useEventSettingsQuery } from '@/registration/hook/useEventSettingsQuery';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { type UserSession } from '@/registration/hook/useUserSession';

interface OrderReviewWithHashProps {
  orderHash: string
  userSession: UserSession | undefined
}

const OrderReviewWithHash: FC<OrderReviewWithHashProps> = ({ orderHash, userSession }) => {
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();
  const { eventId } = useRegisterRoute();
  const { data: eventSettings } = useEventSettingsQuery(eventId);

  const orderQuery = useQuery<ApiResponseSingle<Order>, Error>({
    queryKey: [orderQueryKey, { orderHash }],
    queryFn: async () => { return await loadWaitingForPaymentOrder(createTenantApiRequest)(orderHash); },
    enabled: !!orderHash && !isApiPreparing && !!userSession
  });

  const subchargeLabel = useMemo(
    () => { return eventSettings?.surcharge?.label ?? 'Processing fee'; },
    [eventSettings?.surcharge]
  );

  return (
    <QueryContainer query={orderQuery} errorMessage="Failed to load purchase data.">
      {(orderData) => {
        if (orderData.errors && orderData.errors.length > 0) {
          return <BaseMessageBarError>{orderData.errors.join(', ')}</BaseMessageBarError>;
        }

        return (
          <UiStack spacing={4}>
            <UiHStack justifyContent={'space-between'}>
              <UiText variant={'body1'} fontWeight={600}>
                Personal information
              </UiText>
            </UiHStack>
            <BaseBlockList
              bgColor={'#fff'}
              borderRadius={uiStyles.borderRadius}
              enableDivider={true}
            >
              <UiStack px={8} py={4}>
                <UiText variant={'body2'} color={'text.secondary'}>Name</UiText>
                <UiText>{orderData?.item.attendee.name}</UiText>
                <UiText variant={'body2'} color={'text.secondary'}>Email</UiText>
                <UiText>{orderData?.item.attendee.email}</UiText>
              </UiStack>
            </BaseBlockList>
            <UiHStack justifyContent={'space-between'}>
              <UiText variant={'body1'} fontWeight={600}>
                Your purchase
              </UiText>
            </UiHStack>
            <BaseBlockList
              bgColor={'#fff'}
              borderRadius={uiStyles.borderRadius}
              enableDivider={true}
            >
              {orderData?.item.orderItems.map((item) => {
                return (
                  <UiStack key={item.id} px={8} py={4}>
                    <UiText variant={'body2'} color={'text.secondary'}>{item.name}</UiText>
                    <UiHStack justifyContent={'flex-start'}>
                      <UiText variant={'body2'}>{item.priceFormat} (x {item.quantity ?? 1})</UiText>
                    </UiHStack>
                  </UiStack>
                );
              })}
            </BaseBlockList>
            <UiHStack justifyContent={'flex-end'} flexGrow={1}>
              <UiStack py={2} justifyContent={'flex-end'} px={4}>
                <UiHStack alignItems={'center'} justifyContent={'flex-end'}>
                  <UiText variant={'body1'} color={'text.secondary'}>Subtotal</UiText>
                  <UiText variant={'body1'}>{orderData?.item.subtotalFormat}</UiText>
                </UiHStack>
                <UiHStack alignItems={'center'} justifyContent={'flex-end'}>
                  <UiText variant={'body1'} color={'text.secondary'}>{orderData?.item.taxName}</UiText>
                  <UiText variant={'body1'}>{orderData?.item.taxFormat}</UiText>
                </UiHStack>
                <UiHStack alignItems={'center'} justifyContent={'flex-end'}>
                  <UiText variant={'body1'} color={'text.secondary'}>{subchargeLabel}</UiText>
                  <UiText variant={'body1'}>{orderData?.item.surchargeFormat}</UiText>
                </UiHStack>
                <UiHStack alignItems={'center'} justifyContent={'flex-end'}>
                  <UiText variant={'body1'} color={'text.secondary'}>Total amount</UiText>
                  <UiText variant={'title'}>{orderData?.item.totalPriceFormat}</UiText>
                </UiHStack>
              </UiStack>
            </UiHStack>
          </UiStack>
        );
      }}
    </QueryContainer>
  );
};

export default OrderReviewWithHash;
